import * as React from 'react'
import clx from 'classnames'

import { CompanyIcon } from '@/app/features/icons/Company'
import { faChevronDownSolid, FaIcon, faSitemap } from '@/lib/fa'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { CheckIcon } from '@/app/features/icons/Check'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { CompanyType } from '@/app/constants/companyType'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { buildCompanySwitchPath } from '@/modules/shared/utils/companySwitchPath'

const BadgeCompanyIcon = ({
  isService,
  title,
  className,
}: {
  isService: boolean
  title: string
  className?: string
}) =>
  isService ? (
    <FaIcon icon={faSitemap} title={title} className={className} />
  ) : (
    <CompanyIcon className={className} />
  )

export const CompanyNameBadge = ({fullWidth = false}) => {
  const { t, tSafe } = useLocalize()
  const { companyId } = useParams<{ companyId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const companiesQuery = useQueryCompanies({ includeDeleted: false })
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  useClickOutside({
    ref: dropdownRef,
    handler: () => {
      setOpen(false)
      setQuery('')
    },
  })

  const options = React.useMemo(() => {
    if (!companiesQuery.data) return []
    return companiesQuery.data.map((c) => ({
      label: c.invoiceCompanyName || c.companyName || '',
      value: String(c.id),
      isService: c.type === CompanyType.SERVICE,
    }))
  }, [companiesQuery.data])

  const selected = options.find((o) => o.value === companyId) || null

  const filteredOptions = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = q
      ? options.filter(
          (o) =>
            o.label.toString().toLowerCase().includes(q) ||
            o.value.toString().toLowerCase().includes(q),
        )
      : options

    return [...base].sort((a, b) =>
      selected?.value === a.value ? -1 : selected?.value === b.value ? 1 : 0,
    )
  }, [options, query, selected?.value])

  React.useEffect(() => {
    if (open && options.length > 5) {
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }, [open, options.length])

  if (!selected) return null

  const serviceTitle = tSafe('serviceAccount.title', 'Konto usługowe')

  const closeDropdown = () => {
    setOpen(false)
    setQuery('')
  }

  const handleSelect = (newCompanyId: string) => {
    closeDropdown()
    navigate(buildCompanySwitchPath(location.pathname, newCompanyId, companiesQuery.data))
  }

  return (
    <div ref={dropdownRef} className='tw-relative' >
      <button
        type="button"
        onClick={() =>
          setOpen((v) => {
            if (v) setQuery('')
            return !v
          })
        }
        className={
          `tw-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-px-3 tw-py-2 tw-shadow-sm tw-transition hover:tw-bg-base-200 ${fullWidth ? 'tw-w-full' : ''}`
        }
      >
        <BadgeCompanyIcon
          isService={selected.isService}
          title={serviceTitle}
          className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-content-base"
        />
        <span className="tw-max-w-[200px] tw-truncate tw-text-sm tw-font-semibold tw-text-content-base">
          {selected.label}
        </span>
        {options.length > 1 && (
          <FaIcon
            icon={faChevronDownSolid}
            className={clx(
              'tw-h-3 tw-w-3 tw-shrink-0 tw-text-content-secondary tw-transition-transform',
              open && 'tw-rotate-180',
            )}
          />
        )}
      </button>

      {open && options.length > 1 && (
        <div className="tw-absolute tw-left-0 tw-top-full tw-z-[300] tw-mt-1 tw-w-[270px] tw-rounded-md tw-bg-white tw-shadow-multi">
          {options.length > 5 && (
            <div className="tw-border-b tw-border-content-base tw-border-opacity-10 tw-p-2">
              <div className="tw-relative">
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('common.search')}
                  className="tw-w-full tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-px-3 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-focus-100"
                  onClick={(e) => e.stopPropagation()}
                />

                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('')
                      searchRef.current?.focus()
                    }}
                    className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-rounded hover:tw-bg-base-300"
                  >
                    <FaIcon
                      icon={faClose}
                      className="tw-h-3 tw-w-3 tw-opacity-70"
                    />
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="dropdown-scrollbar tw-max-h-[300px] tw-overflow-y-auto tw-p-1.5">
            {filteredOptions.length === 0 ? (
              <div className="tw-p-2 tw-text-sm tw-opacity-70">Brak wyników</div>
            ) : (
              <ul className="tw-flex tw-flex-col tw-gap-0.5">
                {filteredOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={clx(
                        'tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-px-2 tw-py-2 tw-text-left hover:tw-bg-base-200',
                        selected?.value === option.value &&
                          'tw-bg-secondary tw-font-medium',
                      )}
                    >
                      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                        <BadgeCompanyIcon
                          isService={option.isService}
                          title={serviceTitle}
                          className="tw-h-3 tw-w-3 tw-shrink-0"
                        />
                        <span className="tw-min-w-0 tw-truncate tw-text-sm tw-font-medium tw-text-content-base">
                          {option.label}
                        </span>
                      </span>
                      {selected?.value === option.value && (
                        <CheckIcon className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-focus-100" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
